import React from 'react';
import styled from 'styled-components';
import { BodyText, FlexColumn, FlexRow, PageTile } from '@make-software/cspr-design';
import { TableLoader } from 'app/components/common/table/table-loader';
import { App } from 'app/console-api';
import { InfoSection } from 'app/components/management/common/info-section/info-section';
import { InfoTextInput } from 'app/components/management/common/info-text-input/info-text-input';
import { AppEnvs } from 'config/env';

export interface RawDetailsProps {
  app: App;
}

const StyledWrapper = styled.div`
  font-family: Arial, sans-serif;
`;

const SupportInfo = styled.div(({ theme }) =>
  theme.withMedia({
    width: ['50%', '65%', '100%'],
    marginTop: '20px',
    fontSize: '0.9em'
  })
);

const ApiSnippet = styled.pre`
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-word; // break long words if needed
  overflow-wrap: anywhere; // modern way to ensure wrapping
`;

const IntegrationStatusContainer = styled(FlexColumn)`
  margin: 20px 0;
  font-size: 1.1em;
  color: #212529;
  font-weight: bold;
`;

const StatusDot = styled.div<{ success: boolean }>(({ theme, success }) => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: success
    ? theme.styleguideColors.fillGreen
    : theme.styleguideColors.fillPrimaryRed,
  display: 'inline-block',
  marginRight: '8px'
}));

const StyledSeparator = styled.div<{ margin?: string }>(({ theme, margin }) =>
  theme.withMedia({
    borderBottom: '1px solid ' + theme.styleguideColors.borderPrimary,
    margin: margin ? `${margin} 0` : '16px 0'
  })
);

const StyledUrl = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    ':hover': {
      cursor: 'pointer'
    }
  })
);

export const AppIntegrationDetails = ({ app }: RawDetailsProps) => {
  const uiCode = `import {TaskManagerApp} from "make-traffic-integration-core";

const config = {
    apiUrl: '${AppEnvs.integrationAPIURL}',
    appKey: '${app.appKey}',
};

const taskManager = new TaskManagerApp(config);
await taskManager.init();

// Render list of tasks
taskManager.getTasks('user123').then(tasks => {
    tasks.map(one => {console.log(one.name)})
});
`;

  const curlCommand = `curl -X POST '${app.claimNotificationUrl || `[Your Claim Notification URL]`}?user_id=99' \\
-H 'Authorization: [Your API secret key]' \\
-H 'Content-Type: application/json' \\
-d '{
  "id": "string",
  "targetTaskID": "string",
  "settings": {
    "rewards": [
      {
        "value": 123,
        "type": "coin"
      }
    ]
  }
}'`;

  const handleRedirectUrl = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <StyledWrapper>
      <IntegrationStatusContainer gap={12}>
        <InfoSection
          title={'Helpful Links'}
          content={
            <>
              Please check the{' '}
              <StyledUrl
                onClick={() =>
                  handleRedirectUrl('https://www.npmjs.com/package/make-traffic-integration-core')
                }
                size={3}
                variation={'blue'}
              >
                npm library
              </StyledUrl>{' '}
              and integrate following the examples provided in the{' '}
              <StyledUrl
                onClick={() =>
                  handleRedirectUrl('https://github.com/make-software/make-traffic-integration-lib')
                }
                size={3}
                variation={'blue'}
              >
                GitHub repository.
              </StyledUrl>{' '}
            </>
          }
        ></InfoSection>
        <InfoTextInput
          text={AppEnvs.integrationAPIURL}
          label={'Integration API URL'}
          isCopy={true}
        ></InfoTextInput>
        <InfoTextInput text={app.appKey} label={'App key'} isHidden={true} isCopy={true} />
        <InfoTextInput
          text={app.apiSecretKey}
          label={'API secret key'}
          isHidden={true}
          isCopy={true}
        />
        <StyledSeparator />
      </IntegrationStatusContainer>
      <SupportInfo>
        <InfoSection
          title={'UI integration'}
          content={
            <FlexColumn>
              <BodyText size={1}>
                Below is a sample code snippet to help you get started with integrating the Task
                Manager into your application.
              </BodyText>
              <ApiSnippet>{uiCode}</ApiSnippet>
            </FlexColumn>
          }
        />
      </SupportInfo>
      <SupportInfo>
        <InfoSection
          title={'Backend integration'}
          content={
            <FlexColumn>
              <BodyText size={1}>
                Make Traffic will send HTTP request to Claim Notification URL on successful claim
                event.
              </BodyText>
              <BodyText size={1}>The app should reward user and return 200 response</BodyText>
              <ApiSnippet>{curlCommand}</ApiSnippet>
            </FlexColumn>
          }
        ></InfoSection>
      </SupportInfo>
    </StyledWrapper>
  );
};
